import './styles.css'

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { memo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { useGenerateMentor } from '../../../api/apiHooks'
import { FilterOptions } from '../../../api/types'
import { ImageUploader } from '../../ImageUploader'
import { CustomSelect } from '../../Selects'
import { CompanySelect } from '../CompanySelect'
import {
  GENDER_DEFAULT_VALUE,
  INDUSTRY_DEFAULT_VALUE,
  RACE_DEFAULT_VALUE,
  States,
} from '../constants'
import { DepartmentSelect } from '../DepartmentSelect'
import { JobTitleSelect } from '../JobTitleSelect'

interface IGenerateMentorFormProps {
  onGenerate: (mentorId: string) => void
  closePopup: () => void
  filtersData?: FilterOptions
  setStatus: (status: States) => void
}

export interface Inputs {
  description: string
  firstName: string
  lastName: string
  jobTitle: string
  gender: string
  headline: string
  image: string
  industry: string
  race: string
  department: string
  location: string
  company: string
  mentorPrompt: string
}

const GenerateMentorForm: React.FC<IGenerateMentorFormProps> = memo(
  ({ onGenerate, closePopup, filtersData, setStatus }) => {
    const { mutateAsync, isPending } = useGenerateMentor()

    const methods = useForm<Inputs>({ mode: 'onChange' })

    const {
      register,
      handleSubmit,
      setValue,
      formState: { isValid },
    } = methods

    const onSubmit: SubmitHandler<Inputs> = async data => {
      try {
        const generationResult = await mutateAsync({
          title: data.description,
          company: data.company || undefined,
          department: data.department || undefined,
          firstName: data.firstName || undefined,
          lastName: data.lastName || undefined,
          gender: data.gender || undefined,
          race: data.race || undefined,
          location: data.location || undefined,
          jobTitle: data.jobTitle || undefined,
          headline: data.headline || undefined,
          industry: data.industry || undefined,
          profilePictureUrl: data.image || undefined,
          mentorPrompt: data.mentorPrompt || undefined,
        })
        onGenerate(generationResult.data.id)
      } catch (error) {
        console.error('Error generating companion:', error)
        setStatus('error')
      }
    }

    const onSelectChange = (name: string, value: string) => {
      setValue(name as keyof Inputs, value)
    }

    return (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="text-white">
          <div className="py-2 px-6">
            <div>
              <label className="text-white text-10px ">
                Describe the companion:
              </label>
              <div className="tc-mv-input-wrapper mt-1">
                <textarea
                  {...register('description', {
                    required: true,
                    validate: value => value.trim().length > 0,
                  })}
                  className="w-full resize-none text-black"
                  placeholder="Describe the type of companion you want to create, including their personality, expertise, and any specific characteristics..."
                />
              </div>
            </div>
            <Disclosure defaultOpen>
              <DisclosureButton className="tc-mv-disclosure-button mt-6">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-angle-down" />
                  <span className="text-sm font-semibold">
                    Advanced Options
                  </span>
                </div>
                <div className="text-10px">Optional fields</div>
              </DisclosureButton>
              <DisclosurePanel>
                <div className="mt-1">
                  <label className="text-white text-10px ">
                    Companion prompt:
                  </label>
                  <div className="tc-mv-input-wrapper mt-1">
                    <textarea
                      {...register('mentorPrompt')}
                      className="w-full resize-none text-black"
                      placeholder="Enter the prompt that will define your companion's personality, expertise, and communication style..."
                    />
                  </div>
                </div>
                <div className="mt-1">
                  <label className="text-white text-10px">Profile Image</label>
                  <ImageUploader wrapperClassName="tc-mv-input-wrapper" />
                </div>
                <div className="flex gap-2 mt-1">
                  <div className="w-1/2">
                    <label className="text-white text-10px">First Name</label>
                    <div className="tc-mv-input-wrapper">
                      <input
                        {...register('firstName')}
                        className="w-full border border-gray-300 rounded p-2 text-black"
                        placeholder="John"
                      />
                    </div>
                  </div>
                  <div className="w-1/2">
                    <label className="text-white text-10px">Last Name</label>
                    <div className="tc-mv-input-wrapper">
                      <input
                        {...register('lastName')}
                        className="w-full border border-gray-300 rounded p-2 text-black"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-1/2">
                    <label className="text-white text-10px">Gender</label>
                    <CustomSelect
                      options={[
                        GENDER_DEFAULT_VALUE,
                        ...(filtersData?.gender || []),
                      ]}
                      selectedOption={GENDER_DEFAULT_VALUE}
                      onSelectChange={onSelectChange}
                      name="gender"
                    />
                  </div>
                  <div className="w-1/2">
                    <label className="text-white text-10px">Race</label>
                    <CustomSelect
                      options={[
                        RACE_DEFAULT_VALUE,
                        ...(filtersData?.race || []),
                      ]}
                      selectedOption={RACE_DEFAULT_VALUE}
                      onSelectChange={onSelectChange}
                      name="race"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex gap-2">
                    <div className="w-1/2">
                      <label className="text-white text-10px">Industry</label>
                      <CustomSelect
                        options={[
                          INDUSTRY_DEFAULT_VALUE,
                          ...(filtersData?.industry || []),
                        ]}
                        selectedOption={INDUSTRY_DEFAULT_VALUE}
                        onSelectChange={onSelectChange}
                        name="industry"
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="text-white text-10px">Company</label>
                      <CompanySelect />
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="flex gap-2">
                    <div className="w-1/2">
                      <label className="text-white text-10px">Department</label>
                      <DepartmentSelect />
                    </div>
                    <div className="w-1/2">
                      <label className="text-white text-10px">Job Title</label>
                      <JobTitleSelect />
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <label className="text-white text-10px">Location</label>
                  <div className="tc-mv-input-wrapper">
                    <input
                      {...register('location')}
                      className="w-full border border-gray-300 rounded p-2 text-black"
                      placeholder="New York, USA"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <label className="text-white text-10px">Headline</label>
                  <div className="tc-mv-input-wrapper">
                    <input
                      {...register('headline')}
                      className="w-full border border-gray-300 rounded p-2 text-black"
                      placeholder="Empowering professionals to achieve career success through strategic coaching"
                    />
                  </div>
                </div>
              </DisclosurePanel>
            </Disclosure>
          </div>
          <div className="flex justify-end gap-2 border-t border-white/10 py-4 px-6">
            <button className="btn btn-secondary" onClick={closePopup}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isValid || isPending}
            >
              {isPending && <AnimatedSpinner className="me-2" />}
              Generate companion
            </button>
          </div>
        </form>
      </FormProvider>
    )
  },
)

export { GenerateMentorForm }
